import { useState, useEffect, useMemo } from 'react';
import { X, ChevronLeft, ChevronRight, Copy, Check, Database, Sparkles } from 'lucide-react';
import { combineDescriptions } from '../lib/descriptions';

/**
 * OutputDataModal — Full-width modal showing AI output data in Excel-style tabular view.
 * Props:
 *   items       — full list of filteredItems (for Prev/Next)
 *   currentIndex — index of the currently viewed item
 *   onClose     — close handler
 *   onNavigate  — (newIndex) => navigate to a different item
 */
export default function OutputDataModal({ items, currentIndex, onClose, onNavigate }) {
    const item = items[currentIndex];
    const [copied, setCopied] = useState(false);

    const hasPrev = currentIndex > 0;
    const hasNext = currentIndex < items.length - 1;

    // Parse the AI output
    const parsedOutput = useMemo(() => {
        if (!item?.chatgptOutput) return null;
        try {
            const raw = item.chatgptOutput;
            return typeof raw === 'string' ? JSON.parse(raw) : raw;
        } catch {
            return null;
        }
    }, [item?.chatgptOutput]);

    // Build table rows from parsed output
    const tableRows = useMemo(() => {
        if (!parsedOutput || typeof parsedOutput !== 'object') return [];
        return Object.entries(parsedOutput).map(([key, value]) => ({
            key,
            value: value === null || value === undefined ? '' :
                   typeof value === 'object' ? JSON.stringify(value) : String(value),
            isUrl: typeof value === 'string' && (value.startsWith('http://') || value.startsWith('https://')),
        }));
    }, [parsedOutput]);

    // Keyboard navigation
    useEffect(() => {
        const handleKey = (e) => {
            if (e.key === 'Escape') onClose();
            else if (e.key === 'ArrowLeft' && hasPrev) onNavigate(currentIndex - 1);
            else if (e.key === 'ArrowRight' && hasNext) onNavigate(currentIndex + 1);
        };
        window.addEventListener('keydown', handleKey);
        return () => window.removeEventListener('keydown', handleKey);
    }, [currentIndex, hasPrev, hasNext, onClose, onNavigate]);

    const handleCopy = () => {
        const text = parsedOutput ? JSON.stringify(parsedOutput, null, 2) : String(item?.chatgptOutput || '');
        navigator.clipboard.writeText(text);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    if (!item) return null;

    const partNumber = item.partNumber || item.givenSupplierPartnumber || item.manufacturerNumber || '';
    const description = combineDescriptions(item, ' / ');

    return (
        <div className="fixed inset-0 z-50 flex items-stretch bg-slate-900/50 backdrop-blur-sm animate-fade-in">
            <div className="relative bg-white w-full max-w-6xl mx-auto my-4 rounded-2xl shadow-2xl border border-slate-200 flex flex-col overflow-hidden animate-slide-up">

                {/* ── Header ──────────────────────────────── */}
                <div className="shrink-0 flex items-center justify-between px-6 py-4 bg-gradient-to-r from-slate-50 to-white border-b border-slate-200">
                    <div className="flex items-center gap-3 min-w-0">
                        <div className="w-10 h-10 rounded-xl bg-primary-100 flex items-center justify-center shrink-0">
                            <Sparkles className="w-5 h-5 text-primary-600" />
                        </div>
                        <div className="min-w-0">
                            <h3 className="text-lg font-bold text-slate-800 truncate">
                                AI Output — {partNumber || `Row #${item.rowNumber || item.id}`}
                            </h3>
                            <p className="text-xs text-slate-500">
                                Record {currentIndex + 1} of {items.length}
                                {description && <> · <span className="font-medium text-slate-600">{description.slice(0, 60)}{description.length > 60 ? '…' : ''}</span></>}
                            </p>
                        </div>
                    </div>

                    <div className="flex items-center gap-2 shrink-0">
                        {/* Copy */}
                        <button onClick={handleCopy}
                            className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm font-semibold transition-all
                                ${copied
                                    ? 'bg-emerald-100 text-emerald-700 ring-1 ring-emerald-200'
                                    : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                                }`}>
                            {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                            {copied ? 'Copied!' : 'Copy'}
                        </button>

                        {/* Prev / Next */}
                        <button onClick={() => onNavigate(currentIndex - 1)} disabled={!hasPrev}
                            className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl text-sm font-semibold
                                       bg-slate-100 text-slate-700 hover:bg-slate-200 transition-all
                                       disabled:opacity-30 disabled:cursor-not-allowed">
                            <ChevronLeft className="w-4 h-4" /> Prev
                        </button>
                        <button onClick={() => onNavigate(currentIndex + 1)} disabled={!hasNext}
                            className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl text-sm font-semibold
                                       bg-slate-100 text-slate-700 hover:bg-slate-200 transition-all
                                       disabled:opacity-30 disabled:cursor-not-allowed">
                            Next <ChevronRight className="w-4 h-4" />
                        </button>
                        <div className="w-px h-8 bg-slate-200 mx-1" />
                        <button onClick={onClose} className="p-2 rounded-lg hover:bg-slate-100 transition-colors" title="Close (Esc)">
                            <X size={18} className="text-slate-500" />
                        </button>
                    </div>
                </div>

                {/* ── Body ─────────────────────────────────── */}
                <div className="flex-1 overflow-y-auto">
                    {parsedOutput && tableRows.length > 0 ? (
                        <table className="w-full text-sm">
                            <thead className="sticky top-0 z-10 bg-slate-50 border-b border-slate-200">
                                <tr>
                                    <th className="px-5 py-3 text-left font-bold text-slate-600 uppercase tracking-wider text-xs w-[280px]">Property</th>
                                    <th className="px-5 py-3 text-left font-bold text-slate-600 uppercase tracking-wider text-xs">Value</th>
                                </tr>
                            </thead>
                            <tbody>
                                {tableRows.map((row, i) => (
                                    <tr key={row.key} className={`border-b border-slate-100 hover:bg-primary-50/30 transition-colors
                                        ${i % 2 === 0 ? 'bg-white' : 'bg-slate-50/30'}`}>
                                        <td className="px-5 py-2.5 font-semibold text-slate-700 align-top break-all">
                                            {row.key}
                                        </td>
                                        <td className="px-5 py-2.5 text-slate-800 align-top break-all">
                                            {row.isUrl ? (
                                                <a href={row.value} target="_blank" rel="noopener noreferrer"
                                                   className="text-primary-600 hover:underline">{row.value}</a>
                                            ) : (
                                                row.value || <span className="text-slate-400">—</span>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    ) : (
                        /* Fallback: raw text */
                        <div className="p-6">
                            <pre className="bg-slate-900 text-emerald-300 rounded-xl p-5 overflow-auto text-xs leading-relaxed font-mono"
                                 style={{ maxHeight: '70vh', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                {typeof item.chatgptOutput === 'string'
                                    ? item.chatgptOutput
                                    : JSON.stringify(item.chatgptOutput, null, 2)}
                            </pre>
                        </div>
                    )}
                </div>

                {/* ── Footer ──────────────────────────────── */}
                <div className="shrink-0 px-6 py-3 bg-slate-50/80 border-t border-slate-200 flex items-center justify-between">
                    <span className="text-xs text-slate-500">
                        {tableRows.length} properties · {item.sourceType || 'Online'} source
                        {item.nounModifier && <> · <span className="font-medium">{item.nounModifier}</span></>}
                    </span>
                    <span className="text-xs text-slate-400">
                        ← → Navigate · Esc Close
                    </span>
                </div>
            </div>
        </div>
    );
}
